import {
  Box,
  HStack,
  Stack,
  useBreakpointValue,
  VStack
} from "@chakra-ui/react"
import { CarouselGallery } from "@pathwright/ux/src/components/gallery/Gallery"
import { COVER_PAGE_PADDING } from "../constants"
import CoverPageDivider from "../CoverPageDivider"
import { useCoverPageContext } from "../CoverPageProvider"
import CoverPageBlankSlate, {
  CoverPageAddAnother,
  CoverPageBlankSlateSection
} from "../design/CoverPageBlankSlate"
import { useCoverPageBlankSlate } from "../design/useCoverPageBlankSlate"
import CoverPageSectionTrigger from "../design/CoverPageSectionTrigger"
import { ImmersiveTriggerButton } from "../immersive/CoverPageImmersive"
import PathPreviewNavigator from "./PathPreviewNavigator"
import { PreviewPathCard } from "./PreviewPathCard"
import { PreviewStepCard } from "./PreviewStepCard"
import { PathItems, PreviewItem } from "./types"
import { useCoverPageTranslate } from "../utils"
import { useCoverPagePreviewItems } from "./utils"

const PREVIEW_CARD_MAX_WIDTH = "350px"
const PREVIEW_CARD_HEIGHT = { mobile: "450px", desktop: "600px" }
const PREVIEW_CARD_ASPECT_RATIO = "350 / 600"

const CoverPagePathPreview = () => {
  const missingPreview = useCoverPageBlankSlate("preview")
  const { t } = useCoverPageTranslate()
  const { galleryItems } = useCoverPagePreviewItems()
  const isMobile = useCoverPageContext((v) => v.isMobile)
  const previewSectionRef = useCoverPageContext((v) => v.previewSectionRef)
  const immersiveActive = useCoverPageContext((v) => v.immersiveActive)

  const maxItemHeight = isMobile
    ? PREVIEW_CARD_HEIGHT.mobile
    : PREVIEW_CARD_HEIGHT.desktop
  const computedItemsPerRow =
    useBreakpointValue({
      base: 1,
      md: 2
    }) ?? 1

  if (!galleryItems?.length)
    return (
      <CoverPageBlankSlateSection
        kind="preview"
        title={t("immersive.nav.preview", { defaultValue: "Preview" })}
        sectionRef={previewSectionRef}
        // A Step card's proportions, at the phone card height on every screen
        // so an empty section stays compact.
        w="auto"
        maxW="full"
        h={PREVIEW_CARD_HEIGHT.mobile}
        aspectRatio={PREVIEW_CARD_ASPECT_RATIO}
        align="center"
      />
    )

  const isPathOnly =
    galleryItems.length === 1 && galleryItems[0].type === "path"

  return (
    <VStack position="relative" role="group" id="preview">
      {!immersiveActive && (
        <CoverPageDivider renderTitle={() => <PathPreviewNavigator />} />
      )}
      {!missingPreview && (
        <CoverPageSectionTrigger sectionKey="preview" top="60px" />
      )}
      <HStack
        ref={previewSectionRef}
        className="CoverPagePathPreview"
        position="relative"
        role="group"
        w="100%"
        scrollMarginTop="150px"
        gap={0}
        h={isPathOnly ? "auto" : maxItemHeight}
        maxH="100%"
        css={{
          "& .carousel-item": {
            borderRadius: "card",
            maxWidth: isMobile
              ? `calc(100vw - ${COVER_PAGE_PADDING.md})`
              : PREVIEW_CARD_MAX_WIDTH
          },
          "& .gallery-container, & .carousel-scroller, & .carousel-track, & .carousel-item > .chakra-card":
            {
              height: "100%"
            }
        }}
      >
        {isPathOnly && missingPreview ? (
          // The Path card with an empty slot beside it, shaped like the Step
          // card a previewable Step adds.
          <Stack
            direction={{ base: "column", md: "row" }}
            align="stretch"
            gap={5}
            px={COVER_PAGE_PADDING}
            w="100%"
          >
            <Box
              flex="1"
              minW={0}
              maxW={{ md: PREVIEW_CARD_MAX_WIDTH }}
              maxH={maxItemHeight}
              overflowY="auto"
              borderRadius="card"
            >
              <PreviewPathCard
                data={galleryItems[0].data as PathItems}
                maxItemHeight={maxItemHeight}
              />
            </Box>
            <CoverPageBlankSlate
              kind="preview"
              align="center"
              flex="1"
              maxW={{ md: PREVIEW_CARD_MAX_WIDTH }}
              minH={{ base: "200px", md: maxItemHeight }}
            />
          </Stack>
        ) : isPathOnly ? (
          <Box
            className="CoverPagePathPreview_PathOnly"
            px={
              isMobile
                ? `calc(${COVER_PAGE_PADDING.md} / 2)`
                : COVER_PAGE_PADDING
            }
            w="100%"
          >
            <Box
              maxH="100vh"
              overflowY="auto"
              h="100%"
              w="100%"
              borderRadius="card"
            >
              <PreviewPathCard
                data={galleryItems[0].data as PathItems}
                maxItemHeight={maxItemHeight}
              />
            </Box>
          </Box>
        ) : (
          <CarouselGallery
            items={galleryItems}
            itemsPerRow={computedItemsPerRow}
            maxItemHeight={maxItemHeight}
            itemsFillWidth
            spacingY="0"
            spacingX={
              isMobile
                ? `calc(${COVER_PAGE_PADDING.md} / 2)`
                : COVER_PAGE_PADDING.md
            }
            scrollBehavior={isMobile ? "smooth" : "snap"}
            // To the right of the last card, where another would join it.
            trailing={<CoverPageAddAnother kind="preview" />}
            gap={20}
            enableStagger
            renderItem={(item: PreviewItem) => {
              const previewProps = {
                maxItemHeight
              }
              const content =
                item.type === "path" ? (
                  <PreviewPathCard data={item.data} {...previewProps} />
                ) : (
                  <PreviewStepCard data={item.data} {...previewProps} />
                )

              return (
                <>
                  {content}
                  <Box
                    position="absolute"
                    bottom="10px"
                    left="50%"
                    transform="translateX(-50%)"
                    height="auto"
                    zIndex={10}
                  >
                    <ImmersiveTriggerButton
                      // The Step card's outer link already opens this preview.
                      tabIndex={item.type === "step" ? -1 : undefined}
                      relativeTo={`preview/${
                        item.type === "path" ? "path" : item.data.source_id
                      }`}
                    />
                  </Box>
                </>
              )
            }}
            renderExpander={() => null}
          />
        )}
      </HStack>
    </VStack>
  )
}

export default CoverPagePathPreview
